import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { CheckCircle2, Loader2, RotateCcw, Sparkles, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const AUTO_KEY = "ll_chain_autofix";
const bc = { borderColor: "var(--ll-border)" };

export default function ChainFixDialog({ statementId, onClose, onApplied }) {
  const [diag, setDiag] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [auto, setAuto] = useState(() => localStorage.getItem(AUTO_KEY) === "1");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const started = useRef(false);

  const diagnose = () => api.post(`/statements/${statementId}/chain-diagnose`).then((r) => {
    setDiag(r.data);
    setPicked(new Set(r.data.fixes.map((f) => f.id)));
    return r.data;
  });
  const apply = async (fixes, isUndo = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/statements/${statementId}/chain-fix`, { fixes });
      setResult(isUndo ? null : r.data);
      if (isUndo) { toast.success("Changes undone"); setDiag(null); await diagnose(); }
      onApplied();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't apply the fix"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    diagnose().then((d) => {
      if (localStorage.getItem(AUTO_KEY) === "1" && d.fixes.length) apply(d.fixes);
    }).catch((err) => {
      toast.error(err?.response?.status === 404 ? "That statement no longer exists." : err?.response?.data?.detail || "Diagnosis failed");
      onClose();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementId]);

  const toggleAuto = (v) => { setAuto(v); localStorage.setItem(AUTO_KEY, v ? "1" : "0"); };
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="chain-fix-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles size={16} /> Fix chain break with AI</DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">
            Each statement should begin where the previous one ended. The AI re-reads both files and checks the account number and the transaction math.
          </DialogDescription>
        </DialogHeader>
        {!diag ? (
          <div className="py-8 grid place-items-center gap-2 text-sm text-[var(--ll-ink-soft)]" data-testid="chain-fix-loading">
            <Loader2 className="animate-spin" /> AI is re-reading both statements…
          </div>
        ) : result ? <Result result={result} busy={busy} onUndo={() => apply(result.undo, true)} onClose={onClose} /> : (
          <Findings diag={diag} picked={picked} toggle={toggle} auto={auto} toggleAuto={toggleAuto} busy={busy}
            onApply={() => apply(diag.fixes.filter((f) => picked.has(f.id)))} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Findings({ diag, picked, toggle, auto, toggleAuto, busy, onApply, onClose }) {
  return (
    <div className="space-y-4">
      {diag.difference != null && (
        <div className="text-sm" data-testid="chain-fix-difference">
          Off by <b className="font-mono-fin">{fmtMoney(diag.difference)}</b>{diag.prev_file_name && <> compared with <b>{diag.prev_file_name}</b></>}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1.5">What the AI found</div>
        <ul className="space-y-1.5 text-sm list-disc pl-5" data-testid="chain-fix-findings">
          {diag.findings.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      </div>
      {diag.fixes.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1.5">Suggested fixes</div>
          <ul className="border divide-y" style={bc} data-testid="chain-fix-fixes">
            {diag.fixes.map((f) => (
              <li key={f.id}>
                <label className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--ll-mint)]">
                  <input type="checkbox" className="mt-1" checked={picked.has(f.id)} onChange={() => toggle(f.id)} data-testid={`chain-fix-option-${f.id}`} />
                  {f.label}
                </label>
              </li>
            ))}
          </ul>
          {!diag.resolves && <div className="text-[11px] text-[var(--ll-ochre)] mt-1.5" data-testid="chain-fix-partial">These fixes may not close the whole gap. See the notes above.</div>}
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-[var(--ll-ink-soft)] cursor-pointer">
        <input type="checkbox" checked={auto} onChange={(e) => toggleAuto(e.target.checked)} data-testid="chain-fix-auto-toggle" />
        Apply suggested fixes automatically next time (you can still undo)
      </label>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 px-4 py-2 border text-sm" style={bc} data-testid="chain-fix-cancel">Close</button>
        {diag.fixes.length > 0 && (
          <button onClick={onApply} disabled={busy || !picked.size} data-testid="chain-fix-apply"
            className="flex-1 px-4 py-2 text-sm text-white bg-[var(--ll-ink)] inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Apply {picked.size} fix{picked.size === 1 ? "" : "es"}
          </button>
        )}
      </div>
    </div>
  );
}

function Result({ result, busy, onUndo, onClose }) {
  const fixed = result.chain_status === "ok" || result.chain_status === "first";
  return (
    <div className="space-y-4" data-testid="chain-fix-result">
      <div className={`p-3 text-sm flex items-center gap-2 border-l-4 ${fixed ? "" : "text-[var(--ll-ochre)]"}`}
        style={{ background: fixed ? "#ECFDF5" : "#FFFBEB", borderColor: fixed ? "#059669" : "#D97706" }} data-testid="chain-fix-result-status">
        <CheckCircle2 size={16} className={fixed ? "text-[#059669]" : ""} />
        {fixed ? "Fixed. The statement now connects to the previous one."
          : result.chain_status === "break" ? `Applied, but it's still off by ${fmtMoney(result.difference)}. Check the notes or edit balances manually.`
            : "Applied. The statement was moved and its chain re-checked."}
      </div>
      <div className="flex gap-2">
        <button onClick={onUndo} disabled={busy || !result.undo.length} className="flex-1 px-4 py-2 border text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40" style={bc} data-testid="chain-fix-undo">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Undo
        </button>
        <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-white bg-[var(--ll-ink)]" data-testid="chain-fix-done">Done</button>
      </div>
    </div>
  );
}
